import { CheckCircle, Copy, FileText } from 'lucide-react'
import { useState } from 'react'
import {
  SubtleTabs,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import type {
  CodeExample,
  DocumentationItem,
  DocumentationSection,
  DocumentationStep,
} from '../types/documentation.types'

interface DocumentationContentProps {
  item: DocumentationItem
  section: DocumentationSection
}

export function DocumentationContent({
  item,
  section,
}: DocumentationContentProps) {
  const [activeTab, setActiveTab] = useState(0)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(id)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const renderCodeExample = (codeExample: CodeExample, index: number) => {
    const codeId = `code-${index}`
    const isCopied = copiedCode === codeId

    return (
      <div key={index} className='space-y-2'>
        {codeExample.description && (
          <p className='text-sm text-muted-foreground'>
            {codeExample.description}
          </p>
        )}
        <div className='relative'>
          <div className='flex items-center justify-between bg-gray-900 text-white px-4 py-2 rounded-t-md text-sm'>
            <div className='flex items-center gap-2'>
              <Badge
                variant='outline'
                className='text-xs bg-gray-800 border-gray-700 text-gray-300'
              >
                {codeExample.language}
              </Badge>
              {codeExample.filename && (
                <span className='text-gray-400'>{codeExample.filename}</span>
              )}
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => copyToClipboard(codeExample.code, codeId)}
              className='h-auto p-1 text-gray-400 hover:text-white'
            >
              {isCopied ? (
                <CheckCircle className='h-4 w-4' />
              ) : (
                <Copy className='h-4 w-4' />
              )}
            </Button>
          </div>
          <pre className='bg-gray-900 text-gray-100 p-4 rounded-b-md overflow-x-auto text-sm'>
            <code>{codeExample.code}</code>
          </pre>
        </div>
      </div>
    )
  }

  const renderStep = (step: DocumentationStep) => (
    <div key={step.id} className='space-y-3'>
      <div>
        <h4 className='font-semibold text-lg'>{step.title}</h4>
        {step.description && (
          <p className='text-muted-foreground mt-1'>{step.description}</p>
        )}
      </div>

      {step.codeExample && renderCodeExample(step.codeExample, 0)}

      {step.notes && step.notes.length > 0 && (
        <div className='space-y-1'>
          {step.notes.map((note, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <p key={index} className='text-xs text-muted-foreground'>
              {note}
            </p>
          ))}
        </div>
      )}
    </div>
  )

  const renderTabbedContent = () => {
    if (!item.content.tabs || item.content.tabs.length === 0) {
      return null
    }

    const tabs = item.content.tabs.map(tab => ({
      label: tab.label,
      value: tab.id,
    }))

    const currentTab = item.content.tabs[activeTab]

    return (
      <div className='space-y-6'>
        <SubtleTabs
          value={tabs[activeTab]?.value || tabs[0]?.value}
          onValueChange={(value: string) => {
            const index = tabs.findIndex(tab => tab.value === value)
            setActiveTab(index >= 0 ? index : 0)
          }}
        >
          <SubtleTabsList>
            {tabs.map(tab => (
              <SubtleTabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </SubtleTabsTrigger>
            ))}
          </SubtleTabsList>
        </SubtleTabs>

        <div className='space-y-6'>
          {currentTab.content && (
            <p className='text-muted-foreground'>{currentTab.content}</p>
          )}

          {currentTab.steps && currentTab.steps.length > 0 && (
            <div className='space-y-8'>{currentTab.steps.map(renderStep)}</div>
          )}

          {currentTab.codeExamples && currentTab.codeExamples.length > 0 && (
            <div className='space-y-6'>
              {currentTab.codeExamples.map(renderCodeExample)}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderSimpleContent = () => (
    <div className='space-y-6'>
      {item.content.content && (
        <div className='prose prose-sm max-w-none'>
          <p className='text-muted-foreground leading-relaxed'>
            {item.content.content}
          </p>
        </div>
      )}

      {item.content.codeExamples && item.content.codeExamples.length > 0 && (
        <div className='space-y-6'>
          {item.content.codeExamples.map(renderCodeExample)}
        </div>
      )}
    </div>
  )

  return (
    <div className='h-full overflow-y-auto'>
      <div className='p-6 space-y-6'>
        {/* Header */}
        <div className='space-y-2'>
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <span>{section.title}</span>
            <span>/</span>
            <span>{item.title}</span>
          </div>
          <h1 className='text-2xl font-bold'>{item.title}</h1>
          {item.content.description && (
            <p className='text-muted-foreground text-lg leading-relaxed'>
              {item.content.description}
            </p>
          )}
          <div className='text-xs text-muted-foreground flex items-center gap-1'>
            <span>Last updated: Dec 04, 2024</span>
            <Button
              variant='ghost'
              size='sm'
              className='h-auto p-0 ml-2 text-xs'
            >
              <FileText className='h-3 w-3 mr-1' />
              Copy page
            </Button>
          </div>
        </div>

        {/* Content */}
        <div>
          {item.content.type === 'tabbed'
            ? renderTabbedContent()
            : renderSimpleContent()}
        </div>
      </div>
    </div>
  )
}
